// Touch controls (RALLY_GOAL.md §5 P1-1): drag anywhere in the left half to steer (analog,
// relative to where the thumb landed), pedal buttons on the right (throttle, brake/reverse,
// handbrake), shift buttons when the gearbox is manual, and pause, camera and reset at the top.
// Pointer Events, so a finger, a stylus and a test harness all work the same way.
import { emptyRaw, type RawControls } from '../input/smoothing';
import { h } from './screens';

/** Steering travel (fraction of the screen width) from centre to full lock. */
const STEER_SPAN = 0.14;

export interface TouchActions {
  pause(): void;
  camera(): void;
  reset(): void;
}

export class TouchControls {
  readonly root: HTMLDivElement;
  readonly raw: RawControls = { ...emptyRaw(), digital: false };
  private readonly knob: HTMLDivElement;
  private readonly steerZone: HTMLDivElement;
  private readonly shiftRow: HTMLDivElement;
  private steerId = -1;
  private steerX0 = 0;
  /** A touch has been seen (or the device is touch-only): the controls may show. */
  active: boolean;
  private shown = false;

  constructor(parent: HTMLElement, private readonly actions: TouchActions, touchOnly: boolean) {
    this.active = touchOnly;
    this.root = h('div', 'touch off', undefined, parent);
    this.root.setAttribute('aria-hidden', 'true');
    // Steering: the whole left half; a knob shows where the thumb is.
    this.steerZone = h('div', 'touch-steer', undefined, this.root);
    const track = h('div', 'touch-steer-track', undefined, this.steerZone);
    this.knob = h('div', 'touch-steer-knob', undefined, track);
    this.steerZone.addEventListener('pointerdown', (e) => this.steerDown(e));
    this.steerZone.addEventListener('pointermove', (e) => this.steerMove(e));
    for (const ev of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) this.steerZone.addEventListener(ev, (e) => this.steerUp(e));
    // Pedals.
    const pedals = h('div', 'touch-pedals', undefined, this.root);
    this.hold(h('div', 'touch-btn touch-hand', 'HAND', pedals), (v) => (this.raw.handbrake = v ? 1 : 0));
    this.hold(h('div', 'touch-btn touch-brake', 'BRAKE', pedals), (v) => (this.raw.brake = v ? 1 : 0));
    this.hold(h('div', 'touch-btn touch-gas', 'GAS', pedals), (v) => (this.raw.throttle = v ? 1 : 0));
    this.shiftRow = h('div', 'touch-shift', undefined, this.root);
    this.hold(h('div', 'touch-btn', '−', this.shiftRow), (v) => (this.raw.shiftDown = v));
    this.hold(h('div', 'touch-btn', '+', this.shiftRow), (v) => (this.raw.shiftUp = v));
    // Top bar.
    const top = h('div', 'touch-top', undefined, this.root);
    this.tap(h('div', 'touch-btn touch-small', 'RESET', top), () => this.actions.reset());
    this.tap(h('div', 'touch-btn touch-small', 'CAM', top), () => this.actions.camera());
    const pause = this.tap(h('div', 'touch-btn touch-small touch-pause', '', top), () => this.actions.pause());
    h('span', '', undefined, pause);
    h('span', '', undefined, pause);
    h('div', 'touch-rotate', 'Turn your phone sideways to drive', this.root);
    // Any touch anywhere switches the controls on (a touchscreen laptop, a tablet with a pad…).
    window.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') this.active = true;
    }, { capture: true, passive: true });
  }

  private capture(el: HTMLElement, id: number): void {
    try {
      el.setPointerCapture(id);
    } catch {
      /* synthetic or already-released pointer */
    }
  }

  private hold(el: HTMLElement, set: (on: boolean) => void): void {
    const ids = new Set<number>();
    const update = () => {
      set(ids.size > 0);
      el.classList.toggle('on', ids.size > 0);
    };
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      ids.add(e.pointerId);
      this.capture(el, e.pointerId);
      update();
    });
    for (const ev of ['pointerup', 'pointercancel', 'lostpointercapture'] as const)
      el.addEventListener(ev, (e) => {
        ids.delete(e.pointerId);
        update();
      });
  }

  private tap(el: HTMLElement, fn: () => void): HTMLElement {
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      fn();
    });
    return el;
  }

  private steerDown(e: PointerEvent): void {
    if (this.steerId >= 0) return;
    e.preventDefault();
    this.steerId = e.pointerId;
    this.steerX0 = e.clientX;
    this.capture(this.steerZone, e.pointerId);
    this.steerZone.classList.add('on');
    this.steerMove(e);
  }

  private steerMove(e: PointerEvent): void {
    if (e.pointerId !== this.steerId) return;
    const span = Math.max(60, window.innerWidth * STEER_SPAN);
    const s = Math.max(-1, Math.min(1, (e.clientX - this.steerX0) / span));
    this.raw.steer = s;
    this.knob.style.transform = `translateX(${(s * 100).toFixed(0)}%)`;
  }

  private steerUp(e: PointerEvent): void {
    if (e.pointerId !== this.steerId) return;
    this.steerId = -1;
    this.raw.steer = 0;
    this.knob.style.transform = '';
    this.steerZone.classList.remove('on');
  }

  /** Per frame: show while driving on a touch device; shift buttons only with manual gears. */
  update(driving: boolean, manualGears: boolean): void {
    const show = this.active && driving;
    if (show !== this.shown) {
      this.shown = show;
      this.root.classList.toggle('off', !show);
      document.body.classList.toggle('touch-on', show);
      if (!show) this.release();
    }
    this.shiftRow.style.display = manualGears ? '' : 'none';
  }

  /** Let go of everything (pause, menus). */
  release(): void {
    const r = this.raw;
    r.steer = r.throttle = r.brake = r.handbrake = 0;
    r.shiftUp = r.shiftDown = false;
    this.steerId = -1;
    this.knob.style.transform = '';
    for (const el of this.root.querySelectorAll('.on')) el.classList.remove('on');
  }
}
